import { FetchSubDeviceSnapshotQuery } from '@/lib/api/devices/devices';
import { SearchEventsQuery, FetchEventImageUrlQuery } from '@/lib/api/monitoring/events';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ComponentProps, Ref, useRef, useState } from 'react';
import { AspectRatio } from '../ui/aspect-ratio';
import { Spinner } from '../ui/spinner';
import { cn } from '@/lib/utils';
import useIsVisible from '@/hooks/usIsVisible';
import { Ban, Camera } from 'lucide-react';

export type DeviceSnapshotProps = ComponentProps<'div'> &
  ComponentProps<'img'> & {
    deviceId: string | undefined;
    projectId?: string;
    companyId?: string;
    videoProcessorId?: string | null;
    onLoad?: () => void;
    imgRef?: Ref<HTMLElement>;
    withLoadingText?: boolean;
  };

export const DeviceSnapshot = ({
  deviceId,
  onLoad,
  imgRef,
  projectId,
  companyId,
  videoProcessorId,
  withLoadingText = true,
  ...rest
}: DeviceSnapshotProps) => {
  const ref = useRef<HTMLImageElement>(null);
  const isVisible = useIsVisible(ref);

  const snapshotQuery = useQuery({
    ...FetchSubDeviceSnapshotQuery({ id: deviceId ?? '' }),
    enabled: !!deviceId && isVisible,
  });

  const snapshotErrorStatus = (snapshotQuery.error as AxiosError | undefined)?.response?.status;

  const eventQuery = useQuery({
    ...SearchEventsQuery({
      companyId: companyId ?? '',
      projectId: projectId ?? '',
      videoProcessorId: videoProcessorId ?? undefined,
    }),
    enabled:
      snapshotErrorStatus === 400 &&
      !!companyId &&
      !!projectId &&
      !!videoProcessorId,
  });
  
  const eventImageValue = eventQuery.data?.rows?.[0]?.values?.find((v) => v.type === 'SnapshotImage');
  const eventValueId = eventImageValue?.id;
  
  const eventImageUrlQuery = useQuery({
    ...FetchEventImageUrlQuery(eventValueId ?? ''),
    enabled: !!eventValueId && snapshotErrorStatus === 400,
  });
  
  const base64Snapshot = snapshotQuery.data?.b64Img;
  const fallbackUrl = eventImageUrlQuery.data?.url || eventImageValue?.sourceUrl;
  const snapshot = base64Snapshot ? `data:image/png;base64, ${base64Snapshot}` : fallbackUrl;
  const isLoading = snapshotQuery.isLoading || eventQuery.isLoading || eventImageUrlQuery.isLoading;
  const [invalid, setInvalid] = useState(false);

  if (isLoading && !snapshot) {
    return (
      <AspectRatio
        ratio={16 / 9}
        {...rest}
        className={cn(
          'flex animate-bg-pulse-green flex-col items-center justify-center gap-2',
          rest.className
        )}
        ref={ref}
      >
        <Spinner size={'medium'} className="text-gray-400" />
        {withLoadingText && (
          <span className="text-center font-bold uppercase text-gray-400">Snapshot Loading</span>
        )}
      </AspectRatio>
    );
  }



  if (!snapshot) {
    return (
      <AspectRatio
        ratio={16 / 9}
        {...rest}
        className={cn('flex flex-col items-center justify-center gap-4', rest.className)}
        ref={ref}
      >
        <Camera className="h-12 w-12 text-gray-400" />
      </AspectRatio>
    );
  }

  if (invalid) {
    return (
      <AspectRatio
        ratio={16 / 9}
        {...rest}
        className={cn('flex flex-col items-center justify-center gap-4', rest.className)}
        ref={ref}
      >
        <Ban className="h-12 w-12 text-gray-400" />
        <p className="text-center font-bold uppercase text-gray-400">Snapshot Corrupted</p>
      </AspectRatio>
    );
  }

  return (
    <img
      {...rest}
      ref={ref}
      className={cn('w-full object-contain', rest.className)}
      src={snapshot}
      alt="Camera Snapshot"
      onError={() => setInvalid(true)}
      onLoad={onLoad}
    />
  );
};
